import { Injectable } from '@angular/core';

/**
 * Group (CTA template) counterpart to DeliverableChangeTrackerService/ExecutionChangeTrackerService
 * - see DeliverableChangeTrackerService's doc comment for why dirty state is tracked per-field
 * rather than per-entity. Marked from creators-and-ctas.component.ts's trackGroupChange()/
 * trackGroupRemoval(), the same places that fire the immediate `_changed` events (source: 'name' or
 * 'cta_creators'), so `_changed_saved` (campaign-form.component.ts) can tell "genuinely edited
 * this session" apart from "untouched" without re-diffing values at save time.
 *
 * Unlike the deliverable/execution trackers, this doesn't compare against a session-start value to
 * un-mark a reverted edit (see docs/event-tracking-implementation-map.md's Wave 10 log for why that
 * simplification was accepted) - a group is edited via discrete dialog-close/removal actions, not
 * continuously-live field input, so the same-session edit-then-revert case this would guard against
 * is far less likely to occur in practice than it was for deliverables' live-typed fields.
 *
 * providedIn: 'root' - must be reset by campaign-form.component.ts alongside the other trackers.
 */
@Injectable({
  providedIn: 'root',
})
export class GroupChangeTrackerService {
  // ctaTemplateId -> set of field sources ('name' | 'cta_creators') changed this session.
  private readonly dirtyFieldsByGroup = new Map<string, Set<string>>();
  // LF-5 (audit 2026-08-06) - ctaTemplateIds minted by copyCtaToCreators() this session, so the
  // _saved diff can skip their children's added_saved events (a copied group's children are copy
  // artifacts, not user adds; the group's own added_saved covers the copy). The current copy
  // service preserves the source children's ids so this can't fire today anyway - it's a guard
  // against a future copy-service change.
  private readonly copiedCtaIds = new Set<string>();

  public markFieldChanged(ctaTemplateId: string | undefined, fieldName: string): void {
    if (!ctaTemplateId) {
      return;
    }

    const fields = this.dirtyFieldsByGroup.get(ctaTemplateId) ?? new Set<string>();

    fields.add(fieldName);
    this.dirtyFieldsByGroup.set(ctaTemplateId, fields);
  }

  public isDirty(ctaTemplateId: string): boolean {
    return !!this.dirtyFieldsByGroup.get(ctaTemplateId)?.size;
  }

  public markCtaCopied(ctaTemplateId: string | undefined): void {
    if (ctaTemplateId) {
      this.copiedCtaIds.add(ctaTemplateId);
    }
  }

  public isCtaCopied(ctaTemplateId: string | undefined): boolean {
    return !!ctaTemplateId && this.copiedCtaIds.has(ctaTemplateId);
  }

  public reset(): void {
    this.dirtyFieldsByGroup.clear();
  }

  // Round-4 review (2026-08-06) - copiedCtaIds is deliberately NOT cleared by reset() (which runs
  // inside diffAndTrackSavedGroups, BEFORE the deliverable/execution added passes check
  // isCtaCopied - clearing there would make the LF-5 copy guard a no-op for the very save it
  // exists for). Callers clear it explicitly once the whole _saved diff sequence has run.
  public resetCopiedCtas(): void {
    this.copiedCtaIds.clear();
  }
}
